import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Search, X, Filter } from 'lucide-react';
import apiClient from '../services/api';
import '../styles/MasterForms.css';

const States = () => {
  const getSessionData = () => {
    try {
      const sessionStr = localStorage.getItem('ltms_session');
      if (!sessionStr) return null;
      
      const sessionData = JSON.parse(sessionStr);
      // Handle both direct sessionData and nested sessionData.session
      return sessionData?.session || sessionData;
    } catch (error) {
      console.error('Error parsing session data:', error);
      return null;
    }
  };

  const session = getSessionData();
  const companyId = session?.companyid;
  const yearId = session?.yearid;

  const [states, setStates] = useState([]);
  const [countries, setCountries] = useState([]);
  const [filteredStates, setFilteredStates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountryId, setFilterCountryId] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    stateid: null,
    countryid: '',
    statecode: '',
    statename: '',
    isactive: true
  });
  const [errors, setErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    fetchCountries();
    fetchStates();
  }, []);

  useEffect(() => {
    filterStates();
  }, [searchTerm, filterCountryId, states]);

  const fetchCountries = async () => {
    try {
      const response = await apiClient.get('/Countries.php', {
        params: { active: true }
      });
      
      if (response.data.success) {
        setCountries(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchStates = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/States.php', {
        params: {}
      });
     
      if (response.data.success) {
        setStates(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching states:', error);
      if (error.response?.status === 401) {
        alert('Session expired. Please login again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const filterStates = () => {
    let filtered = states;
    
    // Filter by country
    if (filterCountryId) {
      filtered = filtered.filter(state => 
        state.countryid === parseInt(filterCountryId)
      );
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      filtered = filtered.filter(state =>
        state.statename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.statecode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        state.countryname?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredStates(filtered);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.countryid) {
      newErrors.countryid = 'Country is required';
    }
    
    if (!formData.statename.trim()) {
      newErrors.statename = 'State name is required';
    } else if (formData.statename.length > 100) {
      newErrors.statename = 'State name must be 100 characters or less';
    }
    
    if (formData.statecode && formData.statecode.length > 10) {
      newErrors.statecode = 'State code must be 10 characters or less';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitLoading(true);
    try {
      const method = editMode ? 'put' : 'post';
      const response = await apiClient[method]('/States.php', {
        ...formData,
        countryid: parseInt(formData.countryid),
        isactive: formData.isactive ? 1 : 0
      });
      
      const data = response.data;
      
      if (data.success) {
        alert(editMode ? 'State updated successfully!' : 'State created successfully!');
        setShowModal(false);
        resetForm();
        fetchStates();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error saving state:', error);
      alert('Error saving state. Please try again.');
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleEdit = (state) => {
    setFormData({
      stateid: state.stateid,
      countryid: state.countryid,
      statecode: state.statecode || '',
      statename: state.statename,
      isactive: state.isactive === 1
    });
    setEditMode(true);
    setShowModal(true);
    setErrors({});
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this state?')) {
      return;
    }
    
    try {
      const response = await apiClient.delete('/States.php', {
        data: { stateid: id }
      });
      
      const data = response.data;
      
      if (data.success) {
        alert('State deleted successfully!');
        fetchStates();
      } else {
        alert('Error: ' + data.message);
      }
    } catch (error) {
      console.error('Error deleting state:', error);
      alert('Error deleting state. Please try again.');
    }
  };

  const resetForm = () => {
    setFormData({
      stateid: null,
      countryid: '',
      statecode: '',
      statename: '',
      isactive: true
    });
    setEditMode(false);
    setErrors({});
  };

  const openAddModal = () => {
    resetForm();
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div className="master-container">
      <div className="master-header">
        <div className="header-left">
          <h2>States / Provinces Master</h2>
          <p className="subtitle">Manage state and province information</p>
        </div>
        <button className="btn-primary" onClick={openAddModal}>
          <Plus size={20} />
          Add State
        </button>
      </div>

      <div className="master-toolbar">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Search by state name, code, or country..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="btn-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        
        <div className="filter-group">
          <Filter size={18} />
          <select
            value={filterCountryId}
            onChange={(e) => setFilterCountryId(e.target.value)}
            className="filter-select"
          >
            <option value="">All Countries</option>
            {countries.map(country => (
              <option key={country.countryid} value={country.countryid}>
                {country.countryname}
              </option>
            ))}
          </select>
        </div>
        
        <div className="toolbar-info">
          Total: {filteredStates.length} {filteredStates.length === 1 ? 'state' : 'states'}
        </div>
      </div>

      {loading ? (
        <div className="loading-state">Loading states...</div>
      ) : filteredStates.length === 0 ? (
        <div className="empty-state">
          <p>No states found</p>
          {(searchTerm || filterCountryId) && (
            <button onClick={() => { setSearchTerm(''); setFilterCountryId(''); }}>
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Country</th>
                <th>State Code</th>
                <th>State Name</th>
                <th>Status</th>
                <th>Created At</th>
                <th className="actions-column">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStates.map((state) => (
                <tr key={state.stateid}>
                  <td>
                    <span className="country-badge">
                      {state.countrycode} - {state.countryname}
                    </span>
                  </td>
                  <td className="font-mono">{state.statecode || '-'}</td>
                  <td className="font-semibold">{state.statename}</td>
                  <td>
                    <span className={`status-badge ${state.isactive ? 'active' : 'inactive'}`}>
                      {state.isactive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{new Date(state.createdat).toLocaleDateString()}</td>
                  <td className="actions-column">
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(state)}
                      title="Edit"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(state.stateid)}
                      title="Delete"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit State' : 'Add New State'}</h3>
              <button className="btn-close" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="countryid">
                    Country <span className="required">*</span>
                  </label>
                  <select
                    id="countryid"
                    value={formData.countryid}
                    onChange={(e) => setFormData({ ...formData, countryid: e.target.value })}
                    className={errors.countryid ? 'error' : ''}
                  >
                    <option value="">Select Country</option>
                    {countries.map(country => (
                      <option key={country.countryid} value={country.countryid}>
                        {country.countryname} ({country.countrycode})
                      </option>
                    ))}
                  </select>
                  {errors.countryid && (
                    <span className="error-message">{errors.countryid}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="statecode">State Code</label>
                  <input
                    type="text"
                    id="statecode"
                    value={formData.statecode}
                    onChange={(e) => setFormData({ ...formData, statecode: e.target.value.toUpperCase() })}
                    maxLength="10"
                    placeholder="e.g., CA, TX, FL"
                    className={errors.statecode ? 'error' : ''}
                  />
                  {errors.statecode && (
                    <span className="error-message">{errors.statecode}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="statename">
                  State / Province Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="statename"
                  value={formData.statename}
                  onChange={(e) => setFormData({ ...formData, statename: e.target.value })}
                  maxLength="100"
                  placeholder="e.g., California, Ontario, Dubai"
                  className={errors.statename ? 'error' : ''}
                />
                {errors.statename && (
                  <span className="error-message">{errors.statename}</span>
                )}
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.isactive}
                    onChange={(e) => setFormData({ ...formData, isactive: e.target.checked })}
                  />
                  <span>Active</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={closeModal}
                  disabled={submitLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitLoading}
                >
                  {submitLoading ? 'Saving...' : (editMode ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default States;